import { ExampleWorkday } from './example-workday.model';

/**
 * One coherent Example workday for Aanya.
 * Story: "How did Aanya\'s day actually go?" — balanced context, not a productivity verdict.
 * Keep sales-demo (ssdemo) realm fixtures entirely separate from this trial example.
 */
export const AANYA_EXAMPLE_WORKDAY: ExampleWorkday = {
  dataSource: 'scoped_example',
  employeeName: 'Aanya Mehta',
  employeeRole: 'Product designer',
  dateLabel: 'Tue, 1 Sep 2026',
  timezone: 'Asia/Kolkata',
  managementQuestion: 'How did Aanya\'s day actually go?',
  bannerLabel: 'Example workday — not your company data',
  primaryCta: 'Try SuperSee with one employee',
  primaryCtaSupport: 'Use the same views on one real employee day from your team.',
  exitLabel: 'Exit example',
  finalMessage:
    'That\'s the full loop: see the day, check apps and hours, spot what repeats in Trends, then get a short summary. Next, run it on real work — starting privately on this computer, or with one employee.',
  totals: {
    usage: '6h 18m',
    idle: '1h 22m',
    breaks: '48m',
    firstSeen: '09:12',
    lastSeen: '18:05',
  },
  steps: [
    {
      id: 'replay',
      label: 'Work Replay',
      question: 'How did the day unfold, moment by moment?',
      narrative:
        'Work Replay shows timed blocks with screenshots — focused design work, a review, lunch, research, a quiet stretch, then shipping.',
      takeaway:
        'You can review the shape of a day without watching anyone live.',
      nextHint: 'Next: see which apps and websites carried the day',
    },
    {
      id: 'usage',
      label: 'Apps & Websites',
      question: 'Where did Usage Time go?',
      narrative:
        'Same layout as Apps & Websites in the product: summary KPIs, Top Usage and Daily Trend charts, then the Detailed Usage table.',
      takeaway:
        'Tool mix is context for a conversation, not a scorecard.',
      nextHint: 'Next: see the Timesheet totals for the same day',
    },
    {
      id: 'hours',
      label: 'Timesheet',
      question: 'What does Timesheet show for the day?',
      narrative:
        'Same layout as Timesheet Reports: day-wise KPIs, then a table of first/last activity with Usage, Idle, Offline, and Tracked Time.',
      takeaway:
        'Hours support the story — screenshots and apps tell you what happened.',
      nextHint: 'Next: see Productivity Trends across days',
    },
    {
      id: 'trends',
      label: 'Productivity Trends',
      question: 'What keeps happening across days?',
      narrative:
        'Same layout language as Productivity Trends in the product: Overview (what changed, executive snapshot, KPIs, contributors), Attention Signals, and Deep Work — sample data only.',
      takeaway:
        'This is the flagship management view: patterns, who needs attention, and where deep work actually happened.',
      nextHint: 'Next: see the Daily Insights email',
    },
    {
      id: 'insights',
      label: 'Daily Insights',
      question: 'What lands in your inbox at the end of the day?',
      narrative:
        'Daily Insights is an email snapshot of the team\'s day — who was active, who was out, who needs a look, deep work, communication, distractions, and top apps and websites. Format preview only for this sample.',
      takeaway:
        'Skim the email first; open Replay or Apps only when something needs a closer look.',
      nextHint: '',
    },
  ],
  replayBuckets: [
    {
      range: '09:12—11:40',
      kind: 'usage',
      label: 'Focused morning',
      cards: [
        {
          time: '09:18',
          trigger: 'Periodic',
          appName: 'Figma',
          windowName: 'Checkout flow — v3',
          imageSrc: 'assets/screenshots/8.png',
          note: 'Design work on the primary flow.',
        },
        {
          time: '10:45',
          trigger: 'App switch',
          appName: 'Google Chrome',
          windowName: 'Linear — SPR-214',
          imageSrc: 'assets/screenshots/10.png',
          note: 'Ticket context before the design review.',
        },
      ],
    },
    {
      range: '11:40—12:15',
      kind: 'usage',
      label: 'Team review',
      cards: [
        {
          time: '11:52',
          trigger: 'Periodic',
          appName: 'Google Meet',
          windowName: 'Design review — Checkout',
          imageSrc: 'assets/screenshots/6.png',
          note: 'Live review with product and engineering.',
        },
      ],
    },
    {
      range: '12:15—13:05',
      kind: 'idle',
      label: 'Midday break',
      idleNote: 'Away from the keyboard — a normal lunch gap.',
    },
    {
      range: '13:05—14:35',
      kind: 'usage',
      label: 'Research block',
      cards: [
        {
          time: '13:22',
          trigger: 'Periodic',
          appName: 'Google Chrome',
          windowName: 'Payment patterns — NN/g',
          imageSrc: 'assets/screenshots/10.png',
          note: 'Reference research after the review.',
        },
        {
          time: '14:10',
          trigger: 'App switch',
          appName: 'Figma',
          windowName: 'Checkout flow — v3',
          imageSrc: 'assets/screenshots/8.png',
          note: 'Applying feedback into the file.',
        },
      ],
    },
    {
      range: '14:35—15:20',
      kind: 'idle',
      label: 'Quiet stretch',
      idleNote:
        'Little keyboard activity. SuperSee shows the gap; it does not invent a reason.',
    },
    {
      range: '15:20—18:05',
      kind: 'mixed',
      label: 'Context switching toward ship',
      cards: [
        {
          time: '15:40',
          trigger: 'Periodic',
          appName: 'Slack',
          windowName: '#design — checkout copy',
          imageSrc: 'assets/screenshots/6.png',
          note: 'Aligning copy with marketing.',
        },
        {
          time: '16:55',
          trigger: 'App switch',
          appName: 'Google Chrome',
          windowName: 'Notion — Release notes',
          imageSrc: 'assets/screenshots/10.png',
          note: 'Writing release notes before end of day.',
        },
        {
          time: '17:48',
          trigger: 'Periodic',
          appName: 'Figma',
          windowName: 'Checkout flow — v3',
          imageSrc: 'assets/screenshots/8.png',
          note: 'Final polish before signing off.',
        },
      ],
    },
  ],
  apps: [
    { name: 'Figma', duration: '3h 05m', shareLabel: 'App', kind: 'app' },
    { name: 'Google Chrome', duration: '1h 52m', shareLabel: 'App', kind: 'app' },
    { name: 'Slack', duration: '42m', shareLabel: 'App', kind: 'app' },
    { name: 'Google Meet', duration: '35m', shareLabel: 'App', kind: 'app' },
    { name: 'Notion', duration: '24m', shareLabel: 'App', kind: 'app' },
  ],
  websites: [
    { name: 'figma.com', duration: '2h 48m', shareLabel: 'Website', kind: 'website' },
    { name: 'linear.app', duration: '28m', shareLabel: 'Website', kind: 'website' },
    { name: 'nngroup.com', duration: '22m', shareLabel: 'Website', kind: 'website' },
    { name: 'notion.so', duration: '24m', shareLabel: 'Website', kind: 'website' },
    { name: 'meet.google.com', duration: '35m', shareLabel: 'Website', kind: 'website' },
  ],
  usageFilterLabel: 'All | 26 Aug — 1 Sep 2026',
  usageStats: [
    {
      icon: 'timelapse',
      label: 'Total Active Time',
      value: '6h 18m',
      tone: 'blue',
    },
    {
      icon: 'work_history',
      label: 'Shift Time',
      value: '6h 00m',
      tone: 'indigo',
    },
    {
      icon: 'bolt',
      label: 'Overtime',
      value: '00h 18m',
      tone: 'orange',
    },
    {
      icon: 'desktop_mac',
      label: 'Top App',
      value: 'Figma',
      subtitle: '3h 05m tracked',
      tone: 'purple',
    },
    {
      icon: 'public',
      label: 'Top Website',
      value: 'figma.com',
      subtitle: '2h 48m tracked',
      tone: 'sky',
    },
  ],
  dailyTrend: [
    { label: '26 Aug', appsHours: 5.2, webHours: 2.1 },
    { label: '27 Aug', appsHours: 5.8, webHours: 2.4 },
    { label: '28 Aug', appsHours: 4.1, webHours: 1.8 },
    { label: '29 Aug', appsHours: 1.2, webHours: 0.6 },
    { label: '30 Aug', appsHours: 0.4, webHours: 0.2 },
    { label: '31 Aug', appsHours: 5.6, webHours: 2.3 },
    { label: '1 Sep', appsHours: 5.4, webHours: 3.1 },
  ],
  hours: [
    { label: 'Usage Time', value: '06:18:00' },
    { label: 'Idle Time', value: '01:22:00' },
    { label: 'Offline Time', value: '00:48:00' },
    { label: 'Tracked Time', value: '07:40:00', hint: 'Usage Time plus Idle Time from activity logs.' },
  ],
  timesheetRows: [
    {
      employeeName: 'Aanya Mehta',
      firstActivity: '09:12',
      lastActivity: '18:05',
      usageTime: '06:18:00',
      idleTime: '01:22:00',
      offlineTime: '00:48:00',
      trackedTime: '07:40:00',
      highlight: true,
    },
    {
      employeeName: 'Rohan Kapoor',
      firstActivity: '09:40',
      lastActivity: '17:52',
      usageTime: '05:44:00',
      idleTime: '01:05:00',
      offlineTime: '00:22:00',
      trackedTime: '06:49:00',
    },
    {
      employeeName: 'Priya Nair',
      firstActivity: '10:05',
      lastActivity: '18:18',
      usageTime: '06:02:00',
      idleTime: '00:51:00',
      offlineTime: '00:35:00',
      trackedTime: '06:53:00',
    },
  ],
  insightsHeadline: 'What the email covers',
  insightsPoints: [
    {
      title: 'Team productivity snapshot',
      body: 'Active employees, total time tracked (hours and minutes), active vs idle, and a team activity score.',
    },
    {
      title: 'Absentees and needs attention',
      body: 'Who had no tracked activity, plus people to review today for high idle or a sharp drop vs yesterday.',
    },
    {
      title: 'Deep work and communication',
      body: '90+ minute focus blocks, and the platforms that took the most meeting and chat time.',
    },
    {
      title: 'Distractions, apps, and websites',
      body: 'Where attention went — including an example URL on distractions, plus top apps and websites.',
    },
    {
      title: 'Team performance highlights',
      body: 'Each active person: active vs total time, late by, outside shift, workload, and major activities.',
    },
  ],
  insightsPreviewNote: 'Sample Daily Insights email — not sent for your company.',
  insightsHtmlPath: 'assets/docs/SuperSee_Daily_Insights_for_2026-09-01_SuperSee.html',

  trends: {
    subtitle:
      'Track productivity movement, active vs idle trends, focus patterns, and team changes over time.',
    filterLabel: '18 Aug – 1 Sep',
    filterSummary: 'All teams · All employees · vs previous period',
    healthPill: 'Monitor',
    healthDetail: '1 person needs review · coverage is solid for a 3-person sample',
    recap: [
      {
        tone: 'bad',
        icon: 'trending_down',
        text: 'Idle stayed high for Rohan on 3 of 12 logged days — recurring, not a one-off.',
      },
      {
        tone: 'good',
        icon: 'trending_up',
        text: 'Aanya’s active time and deep-work share held steady this period.',
      },
      {
        tone: 'good',
        icon: 'check_circle',
        text: 'Priya shows a normal pattern — no attention flag this period.',
      },
    ],
    snapshot: [
      {
        label: 'Active Employees',
        value: '3',
        context: 'All sample people logged',
        status: 'Good coverage',
        statusTone: 'good',
        action: 'View people',
      },
      {
        label: 'Productivity Score',
        value: '68%',
        context: 'vs 71% prior period',
        status: 'Monitor',
        statusTone: 'warning',
        action: 'View Trends',
      },
      {
        label: 'Need Attention',
        value: '1',
        context: 'Low activity pattern',
        status: 'Needs review',
        statusTone: 'risk',
        action: 'Review signals',
      },
      {
        label: 'Workload Pressure',
        value: '0',
        context: 'No overload flags',
        status: 'Stable',
        statusTone: 'good',
        action: 'View workload',
      },
      {
        label: 'Deep Work Hours',
        value: '14h 20m',
        context: 'Across 3 people',
        status: 'Healthy',
        statusTone: 'good',
        action: 'View Deep Work',
      },
      {
        label: 'Weekend Work',
        value: '1.2h',
        context: 'Light weekend activity',
        status: 'Low',
        statusTone: 'muted',
        action: 'View patterns',
      },
    ],
    managementSummary:
      'Sample team is mostly steady. One attention signal (Rohan — low activity / high idle) deserves a 1-on-1 with role and shift context. Aanya remains the deep-work anchor for design work.',
    managementDetails: [
      'Comparison window: prior 14 days ending 17 Aug.',
      'Attention uses idle share of logged time and score movement.',
      'Deep Work rule in this sample: 90+ min sessions, ≥70% productive.',
    ],
    kpis: [
      {
        label: 'Productivity / Activity Score',
        value: '68.0%',
        badge: 'Team',
        helper: 'Active ÷ logged time',
        delta: '−3.0 pts vs comparison',
      },
      {
        label: 'Active Time',
        value: '96h 12m',
        helper: 'Across logged employees',
        delta: '+4h 10m',
      },
      {
        label: 'Idle Time',
        value: '38h 40m',
        helper: 'While logged',
        delta: '+2h 05m',
      },
      {
        label: 'Avg Score / Employee',
        value: '66.4%',
        helper: 'Simple average of people',
        delta: '−1.8 pts',
      },
      {
        label: 'Communication Time',
        value: '12.4%',
        helper: 'Of logged time',
        delta: '+0.6 pts',
      },
    ],
    attentionHeadline: '1 person needs a closer look',
    attentionMainSignal: 'Low Activity Pattern',
    recommendedStep: 'Review Attention Signals',
    needingAttention: [
      {
        name: 'Rohan Kapoor',
        initials: 'RK',
        role: 'Engineer',
        score: '44.0%',
        idlePct: '52.0%',
        signal: 'Low Activity Pattern',
        why: 'Idle ran high on 3 logged days — recurring, not a spike.',
        suggested: 'Review with role/shift context',
      },
    ],
    topProductive: [
      {
        name: 'Aanya Mehta',
        initials: 'AM',
        role: 'Product designer',
        score: '72.0%',
        activeTime: '48h 10m',
      },
      {
        name: 'Priya Nair',
        initials: 'PN',
        role: 'PM',
        score: '69.0%',
        activeTime: '41h 20m',
      },
      {
        name: 'Rohan Kapoor',
        initials: 'RK',
        role: 'Engineer',
        score: '44.0%',
        activeTime: '22h 42m',
      },
    ],
    topDeepWork: [
      {
        name: 'Aanya Mehta',
        initials: 'AM',
        role: 'Product designer',
        score: '78%',
        deepWorkPct: '61%',
        deepWorkScore: '78%',
      },
      {
        name: 'Priya Nair',
        initials: 'PN',
        role: 'PM',
        score: '54%',
        deepWorkPct: '32%',
        deepWorkScore: '54%',
      },
      {
        name: 'Rohan Kapoor',
        initials: 'RK',
        role: 'Engineer',
        score: '28%',
        deepWorkPct: '18%',
        deepWorkScore: '28%',
      },
    ],
    attentionQueue: [
      {
        name: 'Rohan Kapoor',
        initials: 'RK',
        role: 'Engineer',
        team: 'Product Engineering',
        logged: '47h 10m',
        active: '22h 42m',
        idlePct: 52,
        deltaIdle: '+8.2',
        commPct: '9%',
        deepWorkPct: '18%',
        status: 'Needs Review',
        statusClass: 'pt-badge pt-badge-warning',
        pills: [{ label: 'Needs Check-in', cssClass: 'pt-pill pt-pill--watch' }],
        reason: 'Low Activity Pattern — idle high on 3 logged days',
      },
      {
        name: 'Priya Nair',
        initials: 'PN',
        role: 'PM',
        team: 'Product',
        logged: '52h 05m',
        active: '41h 20m',
        idlePct: 21,
        deltaIdle: '−1.4',
        commPct: '24%',
        deepWorkPct: '32%',
        status: 'Normal',
        statusClass: 'pt-badge pt-badge-good',
        pills: [],
        reason: 'Within normal range',
      },
      {
        name: 'Aanya Mehta',
        initials: 'AM',
        role: 'Product designer',
        team: 'Design',
        logged: '58h 40m',
        active: '48h 10m',
        idlePct: 18,
        deltaIdle: '−0.6',
        commPct: '11%',
        deepWorkPct: '61%',
        status: 'Normal',
        statusClass: 'pt-badge pt-badge-good',
        pills: [],
        reason: 'Strong focus pattern',
      },
    ],
    deepWorkRule: '90+ min sessions · ≥70% productive · ≤20% distraction',
    deepWorkBanner:
      'Deep Work = sustained focus sessions based on duration, productivity, and distraction rules.',
    deepWorkKpis: [
      { label: 'Total Deep Work Hours', value: '14.3h' },
      { label: 'Deep Work Sessions', value: '11' },
      { label: 'Avg Session Length', value: '1.3h' },
      { label: 'Employees in Deep Work', value: '3' },
    ],
    deepWorkEmployees: [
      {
        name: 'Aanya Mehta',
        initials: 'AM',
        role: 'Product designer',
        deepWorkHours: '9.4h',
        sessions: '6',
        avgSession: '1.6h',
        activeHours: '48.2h',
        totalHours: '58.7h',
        pctOfActive: '61%',
      },
      {
        name: 'Priya Nair',
        initials: 'PN',
        role: 'PM',
        deepWorkHours: '3.5h',
        sessions: '3',
        avgSession: '1.2h',
        activeHours: '41.3h',
        totalHours: '52.1h',
        pctOfActive: '32%',
      },
      {
        name: 'Rohan Kapoor',
        initials: 'RK',
        role: 'Engineer',
        deepWorkHours: '1.4h',
        sessions: '2',
        avgSession: '0.7h',
        activeHours: '22.7h',
        totalHours: '47.2h',
        pctOfActive: '18%',
      },
    ],
    deepWorkSources: [
      { label: 'Figma', hours: '6.8h', pct: 48 },
      { label: 'Google Chrome', hours: '3.1h', pct: 22 },
      { label: 'VS Code', hours: '2.0h', pct: 14 },
      { label: 'Google Meet', hours: '1.4h', pct: 10 },
      { label: 'Other', hours: '1.0h', pct: 6 },
    ],
    drawer: {
      name: 'Aanya Mehta',
      initials: 'AM',
      period: '18 Aug – 1 Sep · vs previous',
      headline: 'Strong design focus when logged (72% active). 48h 10m active across 14 days.',
      subline: 'Deep work 61% of active time · steady vs prior period',
      coverage: [
        'worked', 'worked', 'light', 'worked', 'worked', 'silent', 'silent',
        'worked', 'worked', 'worked', 'light', 'worked', 'worked', 'silent',
      ],
      kpis: [
        { label: 'Days worked', value: '11/14' },
        { label: 'Typical active day', value: '4.4h' },
        { label: 'Productivity', value: '72%' },
      ],
      chips: ['Steady pattern', 'Deep work 61%', 'High focus'],
      categories: [
        { label: 'Design tools', hoursLabel: '23h 10m', sharePct: 48, barPct: 48 },
        { label: 'Browsers & web', hoursLabel: '13h 30m', sharePct: 28, barPct: 28 },
        { label: 'Communication', hoursLabel: '7h 40m', sharePct: 16, barPct: 16 },
        { label: 'Other', hoursLabel: '3h 50m', sharePct: 8, barPct: 8 },
      ],
      topApps: [
        { label: 'Figma', hoursLabel: '18h 20m', sharePct: 38, barPct: 38 },
        { label: 'Google Chrome', hoursLabel: '10h 35m', sharePct: 22, barPct: 22 },
        { label: 'Google Meet', hoursLabel: '5h 45m', sharePct: 12, barPct: 12 },
      ],
    },
  },
};
